import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { ConcernHeader } from './ConcernHeader';
import { ConcernAnalysisResults } from './ConcernAnalysisResults';
import { ConcernChatThread } from './ConcernChatThread';
import { GeneratedDocumentsList } from './GeneratedDocumentsList';

const confColor = (c) => {
  if (c === 'high') return 'bg-green-50 text-green-700 border-green-200';
  if (c === 'medium') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
};

export const ConcernAnalysisSection = ({ caseId, caseData, loadAll }) => {
  const existing = caseData?.last_concern_analysis || null;
  const [analysis, setAnalysis] = useState(existing);
  const [running, setRunning] = useState(false);

  // Conversation state ("Talk to AI")
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [clearingChat, setClearingChat] = useState(false);
  const chatEndRef = useRef(null);

  // Generated documents (per case)
  const [genDocs, setGenDocs] = useState([]);
  const [genDocsLoading, setGenDocsLoading] = useState(false);

  const loadGenDocs = useCallback(async () => {
    setGenDocsLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/generated-documents`);
      setGenDocs(data.documents || []);
    } catch (err) {
      logger.error('Failed to load generated documents:', err);
    } finally {
      setGenDocsLoading(false);
    }
  }, [caseId]);

  const deleteGenDoc = async (docId) => {
    if (!window.confirm('Delete this generated document permanently?')) return;
    try {
      await api.delete(`/cases/${caseId}/generated-documents/${docId}`);
      await loadGenDocs();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  useEffect(() => { loadGenDocs(); }, [loadGenDocs]);

  useEffect(() => { setAnalysis(caseData?.last_concern_analysis || null); }, [caseData?.last_concern_analysis]);

  // Load conversation on mount
  useEffect(() => {
    let active = true;
    api.get(`/cases/${caseId}/concern-chat`).then(r => { if (active) setChatMessages(r.data.messages || []); }).catch(() => {});
    return () => { active = false; };
  }, [caseId]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/concern-analysis`, { question: '' });
      setAnalysis(data.analysis);
      if (loadAll) loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setRunning(false);
    }
  };

  const sendChat = async (e) => {
    if (e) e.preventDefault();
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatLoading(true);
    const now = new Date().toISOString();
    // Optimistic append of user message
    setChatMessages(prev => [...prev, { role: 'user', content: text, created_at: now }]);
    setChatInput('');
    try {
      const { data } = await api.post(`/cases/${caseId}/concern-chat`, { message: text });
      const msgId = data.message_id;
      // Insert a placeholder pending assistant bubble
      setChatMessages(prev => [...prev, { _id: msgId, role: 'assistant', content: '', status: 'pending', created_at: new Date().toISOString() }]);
      // Poll for completion (max ~3 minutes, every 2s)
      let done = false;
      for (let i = 0; i < 90 && !done; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const { data: pollData } = await api.get(`/cases/${caseId}/concern-chat/${msgId}`);
          const m = pollData.message;
          if (m && m.status && m.status !== 'pending') {
            setChatMessages(prev => prev.map(x => (x._id === msgId ? m : x)));
            done = true;
            if (m.generated_document_id) loadGenDocs();
            if (loadAll) loadAll();
            break;
          }
        } catch (pollErr) {
          // transient — keep polling
          logger.debug('concern-chat poll transient error:', pollErr);
        }
      }
      if (!done) {
        setChatMessages(prev => prev.map(x => (x._id === msgId ? { ...x, content: 'Still working… please refresh the page in a few moments to see the reply.', status: 'pending' } : x)));
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.response?.data?.detail || 'request failed'}`, created_at: new Date().toISOString(), _error: true }]);
    } finally {
      setChatLoading(false);
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Clear this conversation? The deep analysis above will remain.')) return;
    setClearingChat(true);
    try {
      await api.delete(`/cases/${caseId}/concern-chat`);
      setChatMessages([]);
    } catch (err) {
      alert('Failed to clear');
    } finally {
      setClearingChat(false);
    }
  };

  const userConcern = (caseData?.description || '').trim();

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="concern-analysis-section">
      <ConcernHeader userConcern={userConcern} running={running} run={run} analysis={analysis} confColor={confColor} />

      {!running && <ConcernAnalysisResults analysis={analysis} />}

      <ConcernChatThread
        caseId={caseId}
        chatMessages={chatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        chatLoading={chatLoading}
        sendChat={sendChat}
        clearChat={clearChat}
        clearingChat={clearingChat}
        userConcern={userConcern}
        chatEndRef={chatEndRef}
      />

      <GeneratedDocumentsList
        caseId={caseId}
        genDocs={genDocs}
        genDocsLoading={genDocsLoading}
        loadGenDocs={loadGenDocs}
        deleteGenDoc={deleteGenDoc}
      />
    </div>
  );
};
